import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { administrativeApi, Client, clientsApi } from "@/lib/api";
import { useCallback, useEffect, useId, useState } from "react";
import { useUser } from "@/contexts/UserContext";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAccountIdSync } from "@/lib/oidcAuth";
import { useToast } from "@/hooks/use-toast";

// Define a type for the raw school data from the API
interface SchoolData {
    clientSchoolIdLex: string | null;
    clientCodeCnx: string;
    clientFantasyName: string | null;
    clientCompanyName: string;
    shippingFee?: number;
    clientCnpj: string;
    clientId: string;
    clientEmail: string;
    clientPhone: string;
    clientAddress: string;
    clientNumber: string;
    clientDistrict: string;
    clientCity: string;
    clientState: string;
    clientPostCode: string;
}

export const SchoolUnit = () => {
    const [ open, setOpen ] = useState( false );
    const [ loading, setLoading ] = useState( false );
    const [ clients, setClients ] = useState<Client[]>( [] );

    const { selectedClient, setSelectedClient } = useUser();

    const { toast } = useToast();

    const listboxId = useId();

    const fetchClients = useCallback( async () => {
        setLoading( true );
        try {
            // Fetch clients from backend API using accountId
            const accountId = getAccountIdSync();
            if ( !accountId ) {
                throw new Error( "Account ID not found in session storage" );
            }

            await administrativeApi.register();

            const { data } = await clientsApi.getSchoolsByAccount( accountId );

            // Map the clients from schools endpoint to display format
            const filteredClients = ( data || [] )
                .filter( ( client: SchoolData ) => client.clientSchoolIdLex !== null )
                .map( ( client: SchoolData ) => ( {
                    codigo_cnx: client.clientCodeCnx,
                    nome_cliente: client.clientFantasyName || client.clientCompanyName,
                    shipping_fee: client?.shippingFee || 0, // shippingFee
                    clientSchoolIdLex: client.clientSchoolIdLex as string, // Already filtered for non-null
                    cnpj: client.clientCnpj,
                    clientId: client.clientId,
                    email: client.clientEmail,
                    telefone: client.clientPhone,
                    endereco: client.clientAddress,
                    numero: client.clientNumber,
                    bairro: client.clientDistrict,
                    cidade: client.clientCity,
                    estado: client.clientState,
                    cep: client.clientPostCode,
                } ) );

            // Enhanced deduplication logic
            const map = new Map();
            const uniqueClients = filteredClients.filter( ( item ) => {
                if ( !map.has( item.codigo_cnx ) ) {
                    map.set( item.codigo_cnx, true );
                    return true;
                }
                return false;
            } );

            setClients( uniqueClients );
        } catch ( error ) {
            console.error( "API error fetching clients:", error );
            // toast( {
            //     title: "Erro ao carregar clientes!",
            //     description:
            //         "Ocorreu um erro ao buscar os clientes. Por favor, tente novamente.",
            //     variant: "destructive",
            // } );
        } finally {
            setLoading( false );
        }
    }, [ toast ] );

    useEffect( () => {
        fetchClients();
    }, [ fetchClients ] );

    // useEffect(() => {
    //     if (selectedClient && clients.length > 0) {
    //         const updatedClient = clients.find(c => c.codigo_cnx === selectedClient.codigo_cnx);
    //         if (updatedClient && JSON.stringify(updatedClient) !== JSON.stringify(selectedClient)) {
    //             setSelectedClient(updatedClient);
    //         }
    //     }
    // }, [clients, selectedClient, setSelectedClient]);

    const isInvalid = !selectedClient;

    return (
        <div className=" flex justify-center items-center gap-4">
            <label htmlFor="client-selector" className={`block min-w-fit text-sm font-medium ${ isInvalid ? 'text-red-700' : '' }`}>
                Unidade escolar:
            </label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        aria-controls={listboxId}
                        aria-busy={loading}
                        aria-label={selectedClient ? `Unidade escolar selecionada: ${ selectedClient.nome_cliente }` : "Selecionar unidade escolar, campo obrigatório"}
                        aria-invalid={isInvalid}
                        className={`w-full justify-between ${ isInvalid ? 'text-red-700 border-red-700 hover:bg-red-600 hover:text-white' : '' }`}
                        disabled={loading}
                        id="client-selector"
                    >
                        {loading
                            ? "Carregando unidades..."
                            : selectedClient
                                ? selectedClient.nome_cliente
                                : "Selecione uma unidade escolar..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent id={listboxId} className="w-full p-0">
                    <Command>
                        <CommandInput
                            placeholder="Pesquisar unidade escolar..."
                            aria-label="Pesquisar unidade escolar"
                        />
                        <CommandList>
                            <CommandEmpty>Nenhuma unidade escolar encontrada.</CommandEmpty>
                            <CommandGroup>
                                {clients.map( ( client ) => (
                                    <CommandItem
                                        key={client.codigo_cnx}
                                        value={client.nome_cliente}
                                        className="cursor-pointer"
                                        onSelect={() => {
                                            setSelectedClient(
                                                client.codigo_cnx === selectedClient?.codigo_cnx
                                                    ? null
                                                    : client
                                            );
                                            setOpen( false );
                                        }}
                                        aria-selected={selectedClient?.codigo_cnx === client.codigo_cnx}
                                    >
                                        <Check
                                            aria-hidden="true"
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                selectedClient?.codigo_cnx === client.codigo_cnx
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        {client.nome_cliente}
                                    </CommandItem>
                                ) )}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    )
};